import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const EMPTY = {
  financial_objectives: "",
  impact_priorities: [],
  preferred_sectors: [],
  geographies: [],
  investment_horizon: "",
  risk_tolerance: "",
  evidence_standard: "",
  additionality_expectations: "",
  notes: "",
};

export default function MandateEditor() {
  const [form, setForm] = useState(EMPTY);
  const [version, setVersion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/mandates/mine");
        if (res.data.mandate) {
          const m = res.data.mandate;
          setForm({
            financial_objectives: m.financial_objectives || "",
            impact_priorities: m.impact_priorities || [],
            preferred_sectors: m.preferred_sectors || [],
            geographies: m.geographies || [],
            investment_horizon: m.investment_horizon || "",
            risk_tolerance: m.risk_tolerance || "",
            evidence_standard: m.evidence_standard || "",
            additionality_expectations: m.additionality_expectations || "",
            notes: m.notes || "",
          });
          setVersion(m.version);
        }
      } catch {
        toast.error("Unable to load mandate.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/mandates/mine", form);
      setVersion(res.data.mandate.version);
      toast.success(`Capital Mandate saved · version ${res.data.mandate.version}`);
    } catch {
      toast.error("Unable to save mandate.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p className="py-16 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Loading mandate…</p>;
  }

  return (
    <div data-testid="mandate-editor">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <h1 className="font-serif-display text-[34px] md:text-[44px] leading-tight tracking-tight text-iv-fg">
            Capital Mandate
          </h1>
          <p className="text-[14.5px] text-iv-fg-2 mt-2 max-w-2xl">
            Every Decision Record is measured against your organization&rsquo;s own mandate — financial
            objectives, impact priorities, horizon, risk tolerance and evidence standards.
          </p>
        </div>
        <span className="iv-chip iv-chip--gold">
          {version ? `Active · v${version}` : "Not yet configured"}
        </span>
      </div>

      <form onSubmit={save} className="iv-panel p-6 md:p-8 space-y-8 max-w-4xl">
        <Field label="Financial Objectives">
          <textarea rows={2} value={form.financial_objectives} onChange={setField("financial_objectives")} placeholder="e.g. Absolute return with capital preservation; IRR 9–12% net" />
        </Field>

        <TagField label="Impact Priorities" values={form.impact_priorities} onChange={(v) => setForm((f) => ({ ...f, impact_priorities: v }))} placeholder="Add a priority and press Enter" />
        <TagField label="Preferred Sectors" values={form.preferred_sectors} onChange={(v) => setForm((f) => ({ ...f, preferred_sectors: v }))} placeholder="Add a sector and press Enter" />
        <TagField label="Geographies" values={form.geographies} onChange={(v) => setForm((f) => ({ ...f, geographies: v }))} placeholder="Add a geography and press Enter" />

        <div className="grid md:grid-cols-2 gap-8">
          <Field label="Investment Horizon">
            <input value={form.investment_horizon} onChange={setField("investment_horizon")} placeholder="e.g. 7–10 years, patient minority" />
          </Field>
          <Field label="Risk Tolerance">
            <input value={form.risk_tolerance} onChange={setField("risk_tolerance")} placeholder="e.g. Moderate; reject binary technology risk" />
          </Field>
          <Field label="Evidence Standard">
            <input value={form.evidence_standard} onChange={setField("evidence_standard")} placeholder="e.g. Independent verification for material impact claims" />
          </Field>
          <Field label="Additionality Expectations">
            <input value={form.additionality_expectations} onChange={setField("additionality_expectations")} placeholder="e.g. Required; financial and behavioural" />
          </Field>
        </div>

        <Field label="Notes">
          <textarea rows={2} value={form.notes} onChange={setField("notes")} placeholder="Any additional principles or constraints" />
        </Field>

        <div className="flex items-center gap-4 pt-2 border-t border-iv-line">
          <button type="submit" disabled={saving} data-testid="mandate-save" className="iv-btn iv-btn--primary">
            {saving ? "Saving…" : version ? "Save New Version" : "Create Mandate"}
          </button>
          <span className="font-mono text-[10.5px] uppercase tracking-widest text-iv-fg-3">
            Saving creates a new version linked to future Decision Records.
          </span>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="iv-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

function TagField({ label, values, onChange, placeholder }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div className="iv-field">
      <span>{label}</span>
      <div className="flex flex-wrap gap-2 mb-2">
        {values.map((v) => (
          <span key={v} className="iv-chip iv-chip--green inline-flex items-center gap-1.5">
            {v}
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={`Remove ${v}`}>
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
        />
        <button type="button" onClick={add} className="iv-btn iv-btn--ghost shrink-0"><Plus size={16} /></button>
      </div>
    </div>
  );
}
